import { authFetch } from 'api/auth/authFetch'
import { useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'

/**
 * Fetches works for a specific contact with pagination and sorting
 * @param {string} contactId - The contact GUID
 * @param {number} count - Number of items per page
 * @param {number} page - Page number (1-based)
 * @param {string} filter - Optional filter criteria
 * @param {string} sort - Optional sort expression
 * @param {string} endpoint - The base API endpoint URL
 * @param {AbortController} controller - Optional abort controller for cancellation
 * @returns {Promise<Object>} Object with total count and results array
 */
export const fetchContactWorks = async (contactId, count, page, filter, sort, endpoint, controller) => {
    if (!contactId || !endpoint) {
        throw new Error('ContactId and endpoint are required')
    }

    // Build query parameters
    const queryParams = new URLSearchParams({
        ...(count && { count: count.toString() }),
        ...(page && { pageNumber: page.toString() }),
        ...(filter && { filter: filter.toString() }),
        ...(sort && { sortby: sort.toString() })
    }).toString()

    const url = `${endpoint}/Contact/${contactId}/Works${queryParams ? `?${queryParams}` : ''}`
    let responseCode = -1

    try {
        const response = await authFetch(url, {
            method: 'GET',
            headers: { "Content-Type": 'application/json' },
            signal: controller?.signal
        })

        if (controller?.signal?.aborted) return null

        responseCode = response.status
        if (responseCode === 200) {
            const data = await response.json()
            return data
        }

        throw {
            Message: 'The Contact Works fetch request was not successful',
            StatusCode: responseCode,
            Details: await response.text()
        }
    } catch (error) {
        if (controller?.signal?.aborted) return null

        throw {
            Message: 'The Contact Works fetch request failed',
            StatusCode: responseCode,
            Details: error
        }
    }
}

/**
 * React component for Contact Works API in Contact Search Select
 * Handles fetching works data with pagination for contacts with >50 works
 */
const ContactSearchWorksApi = (props) => {
    const { contactId, endpoint, setStatusAction, setResultsAction, requestSelector } = props

    const dispatch = useDispatch()
    const apiRequest = useSelector(requestSelector)
    const { currentContactId, count, page, filter, sort, status } = apiRequest

    // Track the last fetched parameters to avoid duplicate fetches
    const lastFetchedRef = useRef(null)

    useEffect(() => {
        // Don't fetch if contactId doesn't match currentContactId
        if (currentContactId !== contactId) {
            return
        }

        // Don't fetch if already pending
        if (status?.status === 'pending') {
            return
        }

        // Create a key representing current fetch parameters
        const currentParamsKey = JSON.stringify({ currentContactId, count, page, filter, sort })

        // Don't fetch if we already fetched with these exact parameters
        if (lastFetchedRef.current === currentParamsKey && status?.status === 'complete') {
            return
        }

        // Remember these parameters
        lastFetchedRef.current = currentParamsKey

        // Set pending status
        dispatch(setStatusAction(contactId, { status: 'pending' }))

        const controller = new AbortController()

        fetchContactWorks(currentContactId, count, page, filter, sort, endpoint, controller)
            .then(results => {
                if (results && !controller.signal.aborted) {
                    dispatch(setResultsAction(contactId, results))
                    dispatch(setStatusAction(contactId, { status: 'complete' }))
                }
            })
            .catch(error => {
                if (!controller.signal.aborted) {
                    console.error('ContactSearchWorksApi: Fetch error', error)
                    dispatch(setResultsAction(contactId, { total: 0, results: [] }))
                    dispatch(setStatusAction(contactId, { status: 'error', error }))
                    // Clear the last fetched key on error so it can retry
                    lastFetchedRef.current = null
                }
            })

        // Cleanup function to abort request if component unmounts
        return () => {
            controller.abort()
        }
    }, [contactId, currentContactId, count, page, filter, sort, endpoint, dispatch, setStatusAction, setResultsAction])

    // This component doesn't render anything
    return null
}

export default ContactSearchWorksApi
